/**
 * sky-swatches.js
 * Custom variant picker for Sky Gazebo products.
 * - Hides Horizon's original picker, injects our compact UI outside the morph zone.
 * - Instant image switching (no AJAX).
 * - Colors: horizontal circle swatches, hover tooltip.
 * - Skeleton: icon + text buttons.
 * - Product data from embedded <script id="sky-product-data"> (works for drafts too).
 */
(function () {
  'use strict';

  // ── Skeleton images ────────────────────────────────────────────────────────────
  const SKELETON_IMG = {
    'Struttura in Acciaio 30mm':   'https://cdn.shopify.com/s/files/1/0994/1644/0964/files/30luk-celik.webp',
    'Struttura in Acciaio 40mm':   'https://cdn.shopify.com/s/files/1/0994/1644/0964/files/40lik-celik.webp',
    'Struttura in Alluminio 40mm': 'https://cdn.shopify.com/s/files/1/0994/1644/0964/files/40lik-aluminyum.webp',
    'Struttura in Alluminio 52mm': 'https://cdn.shopify.com/s/files/1/0994/1644/0964/files/52lik-aluminyum.webp',
  };

  const SKELETON_INFO = {
    'Struttura in Acciaio 30mm':    'Profilato quadrato 30×30 mm. Leggero e compatto, ideale per eventi occasionali e uso sporadico.',
    'Struttura in Acciaio 40mm':    'Profilato quadrato 40×40 mm. Più robusto, adatto a uso frequente e condizioni ventose.',
    'Struttura in Alluminio 40mm':  'Alluminio 40 mm: ultra-leggero e resistente alla corrosione. Ottimo per fiere e mercati.',
    'Struttura in Alluminio 52mm':  'Alluminio 52 mm: la scelta professionale. Massima resistenza per installazioni semi-permanenti.',
  };

  const COLOR_MAP = {
    'Bianco':           '#FFFFFF',
    'Grigio Chiaro':    '#C8C8C8',
    'Grigio':           '#808080',
    'Crema':            '#FFF8DC',
    'Nero':             '#1A1A1A',
    'Kaki':             '#8B7D55',
    'Benetton':         '#009B4D',
    'Bordeaux':         '#7B1C2A',
    'Verde Pistacchio': '#8DC46A',
    'Rosso':            '#CC0000',
    'Blu Navy':         '#0A1045',
    'Lilla':            '#C8A0C8',
    'Blu':              '#1B4FA8',
    'Viola':            '#6B1A7A',
    'Rosa':             '#F4A0B0',
    'Arancione':        '#F47B20',
    'Blu Saks':         '#3D6CB8',
    'Giallo':           '#FFD700',
  };
  const LIGHT_COLORS = new Set(['Bianco', 'Crema', 'Giallo', 'Grigio Chiaro', 'Rosa']);

  // ── Collection page: color handle → Italian name map ─────────────────────────
  const HANDLE_MAP = {
    'bianco': 'Bianco', 'grigio-chiaro': 'Grigio Chiaro', 'grigio': 'Grigio',
    'crema': 'Crema', 'nero': 'Nero', 'kaki': 'Kaki', 'benetton': 'Benetton',
    'bordeaux': 'Bordeaux', 'verde-pistacchio': 'Verde Pistacchio', 'rosso': 'Rosso',
    'blu-navy': 'Blu Navy', 'lilla': 'Lilla', 'blu': 'Blu', 'viola': 'Viola',
    'rosa': 'Rosa', 'arancione': 'Arancione', 'blu-saks': 'Blu Saks', 'giallo': 'Giallo',
  };

  const SKELETON_META = {
    'Struttura in Acciaio 30mm':    { bg: '#607D8B', fg: '#fff' },
    'Struttura in Acciaio 40mm':    { bg: '#455A64', fg: '#fff' },
    'Struttura in Alluminio 40mm':  { bg: '#B8C6CE', fg: '#333' },
    'Struttura in Alluminio 52mm':  { bg: '#CFD8DC', fg: '#333' },
  };

  function tubeSVG(bg, fg) {
    return `<svg width="30" height="30" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg">
      <rect width="30" height="30" rx="5" fill="${bg}"/>
      <rect x="3" y="3" width="24" height="24" rx="3" fill="none" stroke="${fg}" stroke-width="3.5"/>
      <rect x="9" y="9" width="12" height="12" rx="2" fill="${fg}" opacity="0.45"/>
    </svg>`;
  }

  // ── State ──────────────────────────────────────────────────────────────────────
  let productData  = null;
  let selectedOpts = {};   // { 'Colore': 'Bordeaux', 'Struttura': '...' }
  let colorImages  = {};   // colorName → featured_image object
  let preloadCache = {};   // src → Image element

  // ── CSS ────────────────────────────────────────────────────────────────────────
  const CSS = `
    .sky-hide { display: none !important; }

    .sky-picker {
      margin: 16px 0;
      font-family: inherit;
    }
    .sky-picker__label {
      font-size: 13px;
      font-weight: 600;
      letter-spacing: .03em;
      text-transform: uppercase;
      margin-bottom: 10px;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .sky-picker__label span {
      font-weight: 400;
      color: #666;
      font-size: 12px;
      text-transform: none;
      letter-spacing: 0;
    }

    .sky-colors {
      display: flex;
      flex-wrap: wrap;
      gap: 7px;
    }
    .sky-swatch {
      position: relative;
      width: 30px; height: 30px;
      border-radius: 50%;
      cursor: pointer;
      border: 1.5px solid rgba(0,0,0,0.12);
      transition: transform 0.12s ease, box-shadow 0.12s ease;
      flex-shrink: 0;
    }
    .sky-swatch.light { border-color: rgba(0,0,0,0.28); }
    .sky-swatch:hover { transform: scale(1.15); }
    .sky-swatch.active {
      box-shadow: 0 0 0 2px #fff, 0 0 0 4px currentColor;
      transform: scale(1.1);
    }
    .sky-swatch::after {
      content: attr(data-name);
      position: absolute;
      bottom: calc(100% + 7px);
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0,0,0,0.82);
      color: #fff;
      font-size: 11px;
      white-space: nowrap;
      padding: 3px 7px;
      border-radius: 4px;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.15s ease;
      z-index: 10;
    }
    .sky-swatch:hover::after { opacity: 1; }

    /* Skeleton grid: 2 columns desktop, 1 column mobile */
    .sky-skeletons {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
    }
    @media (max-width: 480px) {
      .sky-skeletons { grid-template-columns: 1fr; }
    }
    .sky-ske-btn {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 12px;
      border: 1.5px solid #ddd;
      border-radius: 8px;
      cursor: pointer;
      background: #fff;
      transition: border-color 0.12s ease, background 0.12s ease;
      user-select: none;
      text-align: left;
      width: 100%;
    }
    .sky-ske-btn:hover { border-color: #999; }
    .sky-ske-btn.active {
      border-color: #111;
      background: #f5f5f5;
    }
    .sky-ske-btn img,
    .sky-ske-btn > svg {
      width: 52px;
      height: 52px;
      object-fit: contain;
      border-radius: 6px;
      flex-shrink: 0;
      display: block;
    }
    .sky-ske-label {
      flex: 1;
      font-size: 12px;
      font-weight: 500;
      line-height: 1.35;
    }
    .sky-ske-btn.active .sky-ske-label { font-weight: 700; }

    /* Info icon */
    .sky-ske-info {
      position: relative;
      width: 18px;
      height: 18px;
      flex-shrink: 0;
      cursor: default;
      color: #aaa;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .sky-ske-info:hover { color: #555; }
    .sky-ske-info::after {
      content: attr(data-tip);
      position: absolute;
      bottom: calc(100% + 7px);
      right: 0;
      width: 200px;
      background: rgba(0,0,0,0.85);
      color: #fff;
      font-size: 11px;
      font-weight: 400;
      line-height: 1.45;
      white-space: normal;
      padding: 6px 9px;
      border-radius: 5px;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.15s ease;
      z-index: 20;
    }
    .sky-ske-info:hover::after { opacity: 1; }
  `;

  function injectCSS() {
    if (document.getElementById('sky-picker-css')) return;
    const s = document.createElement('style');
    s.id = 'sky-picker-css';
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  // ── Product data ───────────────────────────────────────────────────────────────

  // Normalize so options is always [{name, values}] regardless of source format.
  // Liquid JSON gives options as string[], fetch gives them as {name,values}[].
  function normalizeProduct(data) {
    if (!data || !Array.isArray(data.options) || !Array.isArray(data.variants)) return null;
    if (data.options.length === 0) return data;

    if (typeof data.options[0] === 'string') {
      const optNames = data.options;
      const valSets = optNames.map(() => []);
      const valSeen = optNames.map(() => new Set());
      data.variants.forEach(v => {
        optNames.forEach((_, i) => {
          const val = v[`option${i + 1}`];
          if (val && !valSeen[i].has(val)) {
            valSets[i].push(val);
            valSeen[i].add(val);
          }
        });
      });
      data.options = optNames.map((name, i) => ({ name, values: valSets[i] }));
    }
    return data;
  }

  async function getProductData(handle) {
    // Try embedded JSON tag first (injected by Liquid — works for draft products)
    const tag = document.getElementById('sky-product-data');
    if (tag) {
      try {
        const normalized = normalizeProduct(JSON.parse(tag.textContent));
        if (normalized) return normalized;
      } catch (_) { /* fall through */ }
    }
    // Fallback: storefront AJAX (only works for published products)
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return normalizeProduct(await res.json());
  }

  function buildColorImages(product) {
    colorImages = {};
    product.variants.forEach(v => {
      const color = v.option1;
      if (!colorImages[color]) colorImages[color] = null;
      if (v.featured_image && !colorImages[color]) {
        colorImages[color] = v.featured_image;
      }
    });
    Object.values(colorImages).filter(Boolean).forEach(img => {
      if (!preloadCache[img.src]) {
        const el = new Image();
        el.src = img.src;
        preloadCache[img.src] = el;
      }
    });
  }

  function findVariant() {
    if (!productData) return null;
    return productData.variants.find(v =>
      productData.options.every((opt, i) => {
        const name = opt.name;
        return !selectedOpts[name] || v[`option${i + 1}`] === selectedOpts[name];
      })
    ) || null;
  }

  // ── DOM updates ────────────────────────────────────────────────────────────────
  function updateMainImage(colorName) {
    const imgData = colorImages[colorName];
    if (!imgData) return;
    const candidates = [
      'media-gallery img',
      '.product__media-item--active img',
      '.product__media img',
      '.product-single__photo img',
      '.product__photo img',
      '[data-product-media] img',
    ];
    for (const sel of candidates) {
      const el = document.querySelector(sel);
      if (el) {
        el.src = imgData.src;
        if (el.srcset) el.srcset = imgData.src + ' 1x';
        return;
      }
    }
  }

  function fmtPrice(cents) {
    return '\u20ac\u00a0' + (cents / 100).toLocaleString('it-IT', { minimumFractionDigits: 2 });
  }

  function updatePickerUI() {
    const variant = findVariant();

    document.querySelectorAll('.sky-swatch').forEach(el =>
      el.classList.toggle('active', el.dataset.value === selectedOpts['Colore'])
    );
    document.querySelectorAll('.sky-ske-btn').forEach(el =>
      el.classList.toggle('active', el.dataset.value === selectedOpts['Struttura'])
    );

    if (variant) {
      const priceDisplay = document.getElementById('sky-price-display');
      if (priceDisplay) priceDisplay.textContent = fmtPrice(variant.price);
      const idInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
      if (idInput) idInput.value = variant.id;
    }
  }

  function selectColor(colorName) {
    selectedOpts['Colore'] = colorName;
    const nameEl = document.getElementById('sky-color-selected');
    if (nameEl) nameEl.textContent = colorName;
    updatePickerUI();
    updateMainImage(colorName);
    const radio = document.querySelector(`input[type="radio"][value="${colorName}"]`);
    if (radio) radio.checked = true;
  }

  function selectSkeleton(value) {
    selectedOpts['Struttura'] = value;
    updatePickerUI();
    const radio = document.querySelector(`input[type="radio"][value="${value}"]`);
    if (radio) radio.checked = true;
  }

  // ── Build UI ───────────────────────────────────────────────────────────────────
  function buildUI(product, insertAfter) {
    const wrapper = document.createElement('div');
    wrapper.className = 'sky-picker';
    wrapper.id = 'sky-custom-picker';

    // Price
    const priceRow = document.createElement('div');
    priceRow.style.cssText = 'font-size:40px;font-weight:700;margin-bottom:14px;';
    priceRow.innerHTML = `<span id="sky-price-display"></span>`;
    wrapper.appendChild(priceRow);

    // Colors
    const colorOption = product.options.find(o => o.name === 'Colore');
    if (colorOption) {
      const labelRow = document.createElement('div');
      labelRow.className = 'sky-picker__label';
      labelRow.innerHTML = `Colore <span id="sky-color-selected">${selectedOpts['Colore'] || ''}</span>`;
      wrapper.appendChild(labelRow);

      const row = document.createElement('div');
      row.className = 'sky-colors';
      colorOption.values.forEach(val => {
        const hex = COLOR_MAP[val] || '#ccc';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sky-swatch' + (LIGHT_COLORS.has(val) ? ' light' : '');
        btn.style.backgroundColor = hex;
        btn.style.color = hex;
        btn.dataset.value = val;
        btn.dataset.name = val;
        btn.setAttribute('aria-label', val);
        btn.addEventListener('click', () => selectColor(val));
        row.appendChild(btn);
      });
      wrapper.appendChild(row);
    }

    // Skeleton
    const skelOption = product.options.find(o => o.name === 'Struttura');
    if (skelOption) {
      const spacer = document.createElement('div');
      spacer.style.marginTop = '18px';
      wrapper.appendChild(spacer);

      const labelRow = document.createElement('div');
      labelRow.className = 'sky-picker__label';
      labelRow.textContent = 'Struttura';
      wrapper.appendChild(labelRow);

      const row = document.createElement('div');
      row.className = 'sky-skeletons';
      skelOption.values.forEach(val => {
        const meta   = SKELETON_META[val];
        const imgUrl = SKELETON_IMG[val];
        const tip    = SKELETON_INFO[val] || '';

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sky-ske-btn';
        btn.dataset.value = val;

        const iconHTML = imgUrl
          ? `<img src="${imgUrl}" alt="${val}">`
          : (meta ? tubeSVG(meta.bg, meta.fg) : '');

        const infoHTML = tip
          ? `<span class="sky-ske-info" data-tip="${tip}" aria-label="Info"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg></span>`
          : '';

        btn.innerHTML = `${iconHTML}<span class="sky-ske-label">${val}</span>${infoHTML}`;
        btn.addEventListener('click', () => selectSkeleton(val));
        row.appendChild(btn);
      });
      wrapper.appendChild(row);
    }

    insertAfter.parentNode.insertBefore(wrapper, insertAfter.nextSibling);
  }

  // ── Collection page: watch for filter AJAX updates and re-run ────────────────
  function setupFilterWatcher() {
    const container = document.querySelector('.product-grid-container');
    if (!container) return;
    let debounce = null;
    new MutationObserver(() => {
      clearTimeout(debounce);
      debounce = setTimeout(initCollectionColorUpdate, 80);
    }).observe(container, { childList: true, subtree: true });
  }

  // ── Collection page: update card images when color filter is active ────────────
  function initCollectionColorUpdate() {
    const scripts = document.querySelectorAll('script.sky-card-colors');
    if (!scripts.length) return;
    const params = new URLSearchParams(location.search);
    const optionColors = params.getAll('filter.v.option.colore');
    const patternColors = params.getAll('filter.p.m.shopify.color-pattern');
    const allColorParams = [...optionColors, ...patternColors];
    if (allColorParams.length !== 1) return;
    const rawColor = allColorParams[0];
    const activeColor = optionColors.length ? rawColor : (HANDLE_MAP[rawColor] || null);
    if (!activeColor) return;
    scripts.forEach(script => {
      let data;
      try { data = JSON.parse(script.textContent); } catch { return; }
      const imgUrl = data.c?.[activeColor];
      if (!imgUrl) return;
      const productId = script.dataset.productId;
      const card = document.querySelector(`product-card[data-product-id="${productId}"]`);
      if (!card) return;
      card.setAttribute('data-featured-media-url', imgUrl);
      const img = card.querySelector('img[loading]') || card.querySelector('img');
      if (img) { img.src = imgUrl; img.srcset = ''; img.removeAttribute('srcset'); }
    });
  }

  // ── Init ───────────────────────────────────────────────────────────────────────
  async function init() {
    const handle = (window.location.pathname.match(/\/products\/([^/?]+)/) || [])[1];
    if (!handle) return;
    if (document.getElementById('sky-custom-picker')) return;

    const picker = document.querySelector('variant-picker');
    if (!picker) return;

    injectCSS();

    try {
      productData = await getProductData(handle);
    } catch (e) {
      console.warn('[sky-swatches] Could not load product data:', e);
      document.getElementById('sky-init')?.remove();
      return;
    }

    if (!productData) {
      document.getElementById('sky-init')?.remove();
      return;
    }

    // Only activate for products that have Colore or Struttura options
    const hasColore    = productData.options.some(o => o.name === 'Colore');
    const hasStruttura = productData.options.some(o => o.name === 'Struttura');
    if (!hasColore && !hasStruttura) {
      document.getElementById('sky-init')?.remove();
      return;
    }

    // Set initial selections from first variant
    productData.options.forEach((opt, i) => {
      const firstVariant = productData.variants[0];
      if (firstVariant) selectedOpts[opt.name] = firstVariant[`option${i + 1}`];
    });

    buildColorImages(productData);
    buildUI(productData, picker);

    // Hide original picker
    picker.classList.add('sky-hide');

    // Hide Horizon's price block (product-price is the custom element in blocks/price.liquid)
    const horizonPrice = document.querySelector('product-price, price-component, .price__container, .product__price');
    if (horizonPrice) horizonPrice.classList.add('sky-hide');

    updatePickerUI();
  }

  function tryInit() {
    if (document.querySelector('variant-picker')) {
      init();
    } else {
      setTimeout(tryInit, 300);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(tryInit, 200);
      initCollectionColorUpdate();
      setupFilterWatcher();
    });
  } else {
    setTimeout(tryInit, 200);
    initCollectionColorUpdate();
    setupFilterWatcher();
  }

  document.addEventListener('shopify:section:load', () => {
    const old = document.getElementById('sky-custom-picker');
    if (old) old.remove();
    productData = null;
    selectedOpts = {};
    colorImages = {};
    setTimeout(tryInit, 300);
  });

})();
